// src/components/ExamQuestionPage.jsx
// Podstrona pojedynczego pytania egzaminacyjnego: /pytania/<slug>.
//
// Każde pytanie dostaje własny adres, własny tytuł i rozszerzone omówienie,
// dzięki czemu odpowiada na konkretną frazę wyszukiwaną przez kandydatów
// („co grozi za porzucenie broni", „ile wynosi opłata za egzamin strzelecki").
// Linkowanie wewnętrzne domyka temat: poprzednie/następne pytanie, pozostałe
// pytania z tej samej sekcji oraz powrót do pełnego zestawu.
import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Seo from '../seo/Seo';
import { breadcrumbJsonLd, faqJsonLd, getLowestPrice } from '../seo/siteMeta';
import {
  getQuestionBySlug,
  getQuestionNeighbours,
  getSiblingQuestions,
  questionPath,
} from '../data/examQuestions';
import { EXAM_PARTS } from '../data/examParts';
import { trackEvent } from '../lib/analytics';
import styles from '../css/modules/ExamQuestions.module.css';

const answerToText = blocks =>
  blocks
    .map(block => (block.type === 'ul' ? block.items.join(' ') : block.text))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

const ExamQuestionPage = () => {
  const { slug } = useParams();
  const question = getQuestionBySlug(slug);

  // Nieznany slug - kierujemy na spis zestawów zamiast pokazywać pustą stronę.
  if (!question) return <Navigate to="/patent-strzelecki" replace />;

  const { prev, next } = getQuestionNeighbours(slug);
  const siblings = getSiblingQuestions(slug);
  const part = EXAM_PARTS.find(item => item.path === question.partPath);
  const explanation = question.explanation;
  const lowestPrice = getLowestPrice();

  // Typ FAQPage, nie QAPage.
  //
  // QAPage opisuje stronę forum, na której pytanie zadaje użytkownik, a odpowiedzi
  // dorzuca społeczność - dlatego Google oczekuje tam pól `author`, `datePublished`
  // i `upvoteCount` (Search Console zgłosiła dokładnie ich brak na tych 136 stronach).
  // Tutaj pytanie i odpowiedź pisze redakcja i odpowiedź jest jedna, autorytatywna,
  // czyli dokładnie przypadek FAQPage. Dopisywanie `upvoteCount` do QAPage byłoby
  // wymyślaniem liczby głosów, których nikt nie oddał.
  const structuredData = [
    faqJsonLd([{ question: question.q, answer: answerToText(question.blocks) }]),
    breadcrumbJsonLd([
      { name: 'Strona główna', path: '/' },
      { name: 'Patent strzelecki', path: '/patent-strzelecki' },
      { name: `Część ${question.partNumber}`, path: question.partPath },
      { name: question.q, path: questionPath(question.slug) },
    ]),
  ];

  // Tytuł to dokładnie treść pytania - tej frazy szukają kandydaci.
  // Opis budujemy z początku odpowiedzi, żeby snippet od razu ją zawierał.
  const answerText = answerToText(question.blocks);
  const description =
    answerText.length > 157
      ? `${answerText.slice(0, 157).replace(/[\s,.;:-]+$/, '')}…`
      : answerText;

  return (
    <div className={styles.page}>
      <Seo
        path={questionPath(question.slug)}
        title={`${question.q} - patent strzelecki`}
        description={description}
        type="article"
        jsonLd={structuredData}
      />

      <div className={styles.inner}>
        <nav className={styles.breadcrumbsNav} aria-label="Ścieżka nawigacji">
          <Link to="/">Strona główna</Link> › <Link to="/patent-strzelecki">Patent strzelecki</Link> ›{' '}
          <Link to={question.partPath}>Część {question.partNumber}</Link>
        </nav>

        <header className={styles.hero}>
          <p className={styles.eyebrow}>
            Pytanie {question.numberInPart} z {question.questionsInPart} · {question.sectionHeading}
          </p>
          <h1 className={styles.h1}>{question.q}</h1>
        </header>

        <div className={styles.answerBox}>
          <p className={styles.answerLabel}>Odpowiedź</p>
          {question.blocks.map((block, index) =>
            block.type === 'ul' ? (
              <ul key={index} className={styles.answerList}>
                {block.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            ) : (
              <p key={index}>{block.text}</p>
            )
          )}
        </div>

        {explanation && (
          <div className={styles.prose}>
            <h2>Dlaczego to pytanie pojawia się na egzaminie</h2>
            <p>{explanation.why}</p>

            <h2>Szerzej o odpowiedzi</h2>
            {explanation.detail.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}

            <div className={styles.mistake}>
              <p className={styles.mistakeLabel}>Najczęstszy błąd</p>
              <p>{explanation.mistake}</p>
            </div>
          </div>
        )}

        <div className={styles.summary}>
          <h2 className={styles.h2}>Sprawdź, czy zapamiętasz</h2>
          <p>
            Pojedyncze pytanie zapamiętuje się na chwilę - dopiero powtórki utrwalają materiał.
            Przerób całą bazę pytań w trybie egzaminu i wracaj do błędnych odpowiedzi.
          </p>
          <div className={styles.ctaRow} style={{ marginTop: 18, marginBottom: 0 }}>
            <Link
              to="/intro"
              className={styles.primaryButton}
              onClick={() => trackEvent('cta_click', { location: 'question_page', label: 'demo' })}
            >
              Rozwiąż darmowy test
            </Link>
            <Link
              to="/cennik"
              className={styles.secondaryButton}
              onClick={() => trackEvent('cta_click', { location: 'question_page', label: 'cennik' })}
            >
              Pełny dostęp od {lowestPrice} zł
            </Link>
          </div>
        </div>

        <nav className={styles.questionPager} aria-label="Sąsiednie pytania">
          {prev ? (
            <Link to={questionPath(prev.slug)} className={styles.pagerLink}>
              <span className={styles.pagerLabel}>← Poprzednie pytanie</span>
              <span className={styles.pagerTitle}>{prev.q}</span>
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link to={questionPath(next.slug)} className={`${styles.pagerLink} ${styles.pagerNext}`}>
              <span className={styles.pagerLabel}>Następne pytanie →</span>
              <span className={styles.pagerTitle}>{next.q}</span>
            </Link>
          ) : (
            <Link to={question.partPath} className={`${styles.pagerLink} ${styles.pagerNext}`}>
              <span className={styles.pagerLabel}>Wróć do zestawu →</span>
              <span className={styles.pagerTitle}>{part ? part.h1 : 'Pełny zestaw pytań'}</span>
            </Link>
          )}
        </nav>

        <section className={styles.siblings}>
          <h2 className={styles.h2}>Pozostałe pytania: {question.sectionHeading}</h2>
          <ul>
            {siblings.map(item => (
              <li key={item.slug}>
                {item.slug === question.slug ? (
                  <span className={styles.currentSibling}>{item.q}</span>
                ) : (
                  <Link to={questionPath(item.slug)}>{item.q}</Link>
                )}
              </li>
            ))}
          </ul>
          <p className={styles.backToPart}>
            <Link to={question.partPath}>
              Zobacz cały zestaw: {part ? part.h1 : `część ${question.partNumber}`}
            </Link>
          </p>
        </section>
      </div>
    </div>
  );
};

export default ExamQuestionPage;
